import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {Subject, takeUntil} from 'rxjs';
import * as fromRoot from '../../../../../state/app.state';
import * as FoodGuideActions from '../../core/food-guide.actions';
import * as FoodGuideSelectors from '../../core/food-guide.selectors';
import {FoodGuideItem} from '../../model/food-guide.model';
import {CommonService} from '../../../../shared/services/common.service';
import {FetchFoodCategoriesRequest} from '../../core/food-guide.actions';

@Component({
  selector: 'app-food-guide-form',
  templateUrl: './food-guide-form.component.html',
  styleUrls: ['./food-guide-form.component.css']
})
export class FoodGuideFormComponent implements OnInit, OnDestroy {
  @Input() data: any;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  mode: 'create' | 'edit' = 'create';
  item: FoodGuideItem | null = null;
  saving = false;

  colorOptions = [
    {key: 'Green', value: 'Green'},
    {key: 'Yellow', value: 'Yellow'},
    {key: 'Red', value: 'Red'},
  ];

  categoryOptions: {key: number, value: string}[] = [];

  form = new FormGroup<any>({
    fgId: new FormControl({value: null, disabled: false}, [Validators.required, Validators.min(1)]),
    name: new FormControl({value: null, disabled: false}, [Validators.required, Validators.maxLength(255)]),
    searchTerms: new FormControl({value: null, disabled: false}, [Validators.maxLength(500)]),
    color: new FormControl({value: null, disabled: false}, [Validators.required]),
    category: new FormControl({value: null, disabled: false}),
    servingAmount: new FormControl({value: null, disabled: false}, [Validators.required, Validators.maxLength(50)]),
    servingUnit: new FormControl({value: null, disabled: false}, [Validators.required, Validators.maxLength(50)]),
  });

  errorMessages: any = {};
  errorMessageMap = {
    fgId: {
      required: 'FG ID is required.',
      min: 'FG ID must be greater than 0.',
    },
    name: {
      required: 'Food name is required.',
      maxlength: 'Max 255 characters.',
    },
    searchTerms: {
      maxlength: 'Max 500 characters.',
    },
    color: {
      required: 'Color is required.',
    },
    category: {},
    servingAmount: {
      required: 'Serving amount is required.',
      maxlength: 'Max 50 characters.',
    },
    servingUnit: {
      required: 'Serving unit is required.',
      maxlength: 'Max 50 characters.',
    },
  };

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
  ) {}

  ngOnInit(): void {
    this.mode = this.data?.data?.mode || 'create';
    this.item = this.data?.data?.item || null;

    if (this.item) {
      this.form.patchValue({
        fgId: this.item['fg_id'] || this.item['fgId'],
        name: this.item.name,
        searchTerms: this.item['search_terms'] || this.item.searchTerms,
        color: this.item.color,
        category: this.item.category || null,
        servingAmount: this.item['serving_amount'] || this.item.servingAmount,
        servingUnit: this.item['serving_unit'] || this.item.servingUnit,
      });
    }

    this.store.select(FoodGuideSelectors.foodCategories)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(categories => {
        this.categoryOptions = categories || [];
      });

    this.store.dispatch(FetchFoodCategoriesRequest());

    this.store.select(FoodGuideSelectors.saving)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(saving => {
        this.saving = saving;
      });
  }

  checkForErrors(currentField?: string): void {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  save(): void {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const formValue = this.form.getRawValue();

    if (this.mode === 'create') {
      this.store.dispatch(FoodGuideActions.CreateFoodGuideItemRequest({
        payload: formValue
      }));
    } else if (this.item) {
      this.store.dispatch(FoodGuideActions.UpdateFoodGuideItemRequest({
        payload: {id: this.item.id, fgId: formValue.fgId, ...formValue}
      }));
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
